import { BigDecimal } from 'effect';

import type { CurrencyT } from '../../../../config/currency';

import { IntlMoney } from '../Intl';

export type IntlMoneyFromPriceProps = {
  readonly value: BigDecimal.BigDecimal;
  readonly currency: CurrencyT;
  readonly roundingMode?: 'floor' | 'ceil';
};

/**
 * IntlMoneyFromPrice - Display money from BigDecimal price value
 *
 * Converts BigDecimal price to cents (API format) using BigDecimal arithmetic,
 * then formats as currency using IntlMoney component.
 *
 * @param value - BigDecimal price value (e.g., 125.99 for $125.99)
 * @param currency - Currency code (e.g., 'EUR', 'USD')
 * @param roundingMode - 'floor' for min values (default), 'ceil' for max values
 *
 * @example
 * // Display min price (floor)
 * <IntlMoneyFromPrice value={minPrice} currency="EUR" roundingMode="floor" />
 *
 * // Display max price (ceil)
 * <IntlMoneyFromPrice value={maxPrice} currency="EUR" roundingMode="ceil" />
 */
export function IntlMoneyFromPrice({
  value,
  currency,
  roundingMode = 'floor',
}: IntlMoneyFromPriceProps) {
  // Convert BigDecimal to cents using BigDecimal arithmetic
  const hundred = BigDecimal.fromNumberUnsafe(100);
  const centsBD = BigDecimal.multiply(value, hundred);

  // Apply rounding: floor for min, ceil for max
  const centsRounded =
    roundingMode === 'ceil' ? BigDecimal.ceil(centsBD) : BigDecimal.floor(centsBD);
  const cents = BigDecimal.toNumberUnsafe(centsRounded);

  return <IntlMoney value={{ amount: cents, currency }} />;
}
